import { create } from "zustand";

import {
  getConsumerReferralSummary,
  type ConsumerReferralSummary,
} from "../lib/referral-links-api";

// Shared consumer-referral summary (share URL + tracking + live incentive).
//
// Why a module-level store rather than per-hook state (the ADR-027 readiness
// idiom): the referral discovery surfaces — dashboard banner, post-publish
// prompt, waitlist-card share sheet, Settings card — can mount together, and
// each one needs the SAME minted link. `getConsumerReferralSummary` calls the
// server's reuse-or-mint endpoint, which is list-then-mint with no uniqueness
// guard, so two concurrent first-ever calls can mint two tokens and render two
// different URLs on one page. One in-flight promise per session makes "the
// banner's URL is the Settings card's URL" true by construction.
//
//   * `ensureLoaded(getToken)` — fetch on first request; concurrent callers
//     share the in-flight promise; a settled success is never refetched.
//   * `refresh(getToken)` — explicit re-issue (the hook's `retry`). A prior
//     failure is NOT cached: `hasLoaded` stays false on error so the next
//     surface to mount, or a retry, re-issues the request.
//   * `reset()` — sign-out / test isolation.
type ReferralStore = {
  summary: ConsumerReferralSummary | null;
  error: Error | null;
  isFetching: boolean;
  hasLoaded: boolean;

  ensureLoaded: (getToken: () => Promise<string | null>) => Promise<void>;
  refresh: (getToken: () => Promise<string | null>) => Promise<void>;
  reset: () => void;
};

let inFlight: Promise<void> | null = null;

async function runFetch(
  getToken: () => Promise<string | null>,
  set: (partial: Partial<ReferralStore>) => void,
): Promise<void> {
  set({ isFetching: true, error: null });
  try {
    const summary = await getConsumerReferralSummary(getToken);
    set({ summary, error: null, hasLoaded: true, isFetching: false });
  } catch (e) {
    // Never keep a stale link around beside an error — the surfaces must
    // show a truthful retry state, not a URL that may no longer be minted.
    set({
      summary: null,
      error: e instanceof Error ? e : new Error(String(e)),
      hasLoaded: false,
      isFetching: false,
    });
  }
}

export const useReferralStore = create<ReferralStore>((set, get) => ({
  summary: null,
  error: null,
  isFetching: false,
  hasLoaded: false,

  ensureLoaded: async (getToken) => {
    if (get().hasLoaded) return;
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  refresh: async (getToken) => {
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  reset: () => {
    inFlight = null;
    set({ summary: null, error: null, hasLoaded: false, isFetching: false });
  },
}));
